import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { safePrint } from "@/utils/safePrint";
import { DateInputDDMonYY } from "@/components/DateInputDDMonYY";
import { formatDateDDMonYY } from "@/utils/dateFormat";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  FileSpreadsheet, FileText, Edit, Trash2, Plus, Save, Loader2,
  ChevronDown, ChevronRight, Eye
} from "lucide-react";

const formatCurrency = (val) => {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val || 0);
};
const formatDateShort = formatDateDDMonYY;

export default function LedgerDialog({ open, onOpenChange, initialParty }) {
  // Party selection
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [ledgerParty, setLedgerParty] = useState('');
  const [ledgerData, setLedgerData] = useState(null);
  const [loadingLedger, setLoadingLedger] = useState(false);

  // Date filters
  const [ledgerFromDate, setLedgerFromDate] = useState('');
  const [ledgerToDate, setLedgerToDate] = useState('');

  // Receipt edit
  const [editingReceiptDate, setEditingReceiptDate] = useState(null);
  const [newReceiptDate, setNewReceiptDate] = useState('');
  const [editingReceiptAmount, setEditingReceiptAmount] = useState(null);
  const [newReceiptAmount, setNewReceiptAmount] = useState('');
  const [ledgerEditReceipt, setLedgerEditReceipt] = useState(null);
  const [ledgerEditForm, setLedgerEditForm] = useState({});

  // Journal / Add transaction
  const [showAddTxn, setShowAddTxn] = useState(false);
  const [savingJournal, setSavingJournal] = useState(false);
  const [journalForm, setJournalForm] = useState({ date: '', amount: '', from_account: '', to_account: '', reference: '', narration: '', order_numbers: '', dispatch_numbers: '' });

  // Bill detail
  const [billDetailOpen, setBillDetailOpen] = useState(false);
  const [billDetailOrder, setBillDetailOrder] = useState(null);
  const [loadingBill, setLoadingBill] = useState(false);

  useEffect(() => {
    if (open) {
      setSearch('');
      setLedgerData(null);
      setLedgerFromDate('');
      setLedgerToDate('');
      axios.get(`${API}/customers`).then(r => setCustomers(r.data || [])).catch(() => {});
      if (initialParty) {
        setLedgerParty(initialParty);
        setSearch(initialParty);
        // Auto-open the ledger for this party
        setTimeout(() => openLedger(initialParty), 300);
      } else {
        setLedgerParty('');
      }
    }
  }, [open, initialParty]);

  const openLedger = async (partyName) => {
    setLoadingLedger(true);
    try {
      const res = await axios.get(`${API}/ledger/${encodeURIComponent(partyName)}`);
      setLedgerData(res.data);
      setLedgerParty(partyName);
    } catch (e) {
      toast.error("Failed to fetch ledger");
      setLedgerData(null);
    }
    setLoadingLedger(false);
  };

  const refreshLedger = () => { if (ledgerParty) openLedger(ledgerParty); };

  const updateReceiptDate = async (receiptId, newDate) => {
    try {
      await axios.put(`${API}/receipts/${receiptId}`, { date: newDate });
      toast.success("Receipt date updated");
      refreshLedger();
    } catch (e) { toast.error("Failed to update date"); }
    setEditingReceiptDate(null);
  };

  const updateReceiptAmount = async (receiptId, amount) => {
    try {
      await axios.put(`${API}/receipts/${receiptId}`, { amount: parseFloat(amount) });
      toast.success("Receipt amount updated");
      refreshLedger();
    } catch (e) { toast.error("Failed to update amount"); }
    setEditingReceiptAmount(null);
  };

  const reverseReceipt = async (receiptId) => {
    if (!window.confirm("Reverse this receipt?")) return;
    try {
      await axios.delete(`${API}/receipts/${receiptId}`);
      toast.success("Receipt reversed");
      refreshLedger();
    } catch (e) { toast.error("Failed to reverse receipt"); }
  };

  const openLedgerReceiptEdit = async (receiptId) => {
    try {
      const res = await axios.get(`${API}/receipts`);
      const receipt = (res.data || []).find(r => r.id === receiptId);
      if (receipt) {
        setLedgerEditReceipt(receiptId);
        setLedgerEditForm({
          date: receipt.date || '', amount: String(receipt.amount || ''),
          debit_account: receipt.debit_account || '', reference: receipt.reference || '',
          narration: receipt.narration || '', order_numbers: (receipt.order_numbers || []).join(', ')
        });
      }
    } catch (e) { toast.error("Failed to load receipt"); }
  };

  const saveLedgerReceiptEdit = async () => {
    try {
      await axios.put(`${API}/receipts/${ledgerEditReceipt}`, {
        ...ledgerEditForm,
        amount: parseFloat(ledgerEditForm.amount),
        order_numbers: ledgerEditForm.order_numbers ? ledgerEditForm.order_numbers.split(',').map(s => s.trim()).filter(Boolean) : []
      });
      toast.success("Receipt updated");
      setLedgerEditReceipt(null);
      refreshLedger();
    } catch (e) { toast.error("Failed to update receipt"); }
  };

  const saveJournalEntry = async () => {
    if (!journalForm.amount || !journalForm.date) { toast.error("Date and amount required"); return; }
    setSavingJournal(true);
    try {
      await axios.post(`${API}/receipts`, {
        date: journalForm.date,
        amount: parseFloat(journalForm.amount),
        credit_account: journalForm.from_account || ledgerParty,
        debit_account: journalForm.to_account || 'Bank',
        reference: journalForm.reference,
        narration: journalForm.narration,
        order_numbers: journalForm.order_numbers ? journalForm.order_numbers.split(',').map(s => s.trim()).filter(Boolean) : [],
        dispatch_numbers: journalForm.dispatch_numbers ? journalForm.dispatch_numbers.split(',').map(s => s.trim()).filter(Boolean) : []
      });
      toast.success("Transaction saved");
      setJournalForm({ date: '', amount: '', from_account: '', to_account: '', reference: '', narration: '', order_numbers: '', dispatch_numbers: '' });
      refreshLedger();
    } catch (e) { toast.error("Failed to save transaction"); }
    setSavingJournal(false);
  };

  const viewBillDetail = async (orderNumber) => {
    setLoadingBill(true);
    setBillDetailOpen(true);
    try {
      const res = await axios.get(`${API}/orders`);
      const order = (res.data || []).find(o => o.order_number === orderNumber);
      setBillDetailOrder(order || null);
    } catch (e) { setBillDetailOrder(null); }
    setLoadingBill(false);
  };

  const printLedger = () => {
    if (!ledgerData || !ledgerParty) return;
    let entries = ledgerData.entries || [];
    if (ledgerFromDate) entries = entries.filter(e => e.date >= ledgerFromDate);
    if (ledgerToDate) entries = entries.filter(e => e.date <= ledgerToDate);
    const dateRange = (ledgerFromDate || ledgerToDate)
      ? `(${ledgerFromDate ? formatDateShort(ledgerFromDate) : 'Start'} to ${ledgerToDate ? formatDateShort(ledgerToDate) : 'Today'})`
      : '';
    const htmlContent = `<html><head><title>Ledger_${ledgerParty}</title>
      <style>body{font-family:Arial;font-size:11px;margin:20px}table{width:100%;border-collapse:collapse;margin-top:10px}th,td{border:1px solid #ddd;padding:4px 6px;text-align:left}th{background:#f0f0f0;font-weight:bold}.right{text-align:right}.bold{font-weight:bold}.red{color:#c00}.green{color:#080}h2{margin-bottom:2px}p{margin:2px 0;color:#666;font-size:10px}</style>
      </head><body>
      <h2>Ledger Statement — ${ledgerParty}</h2>
      <p>${dateRange}</p>
      <table><thead><tr><th>Date</th><th>Description</th><th>Order#</th><th>Dispatch#</th><th class="right">Debit</th><th class="right">Credit</th><th class="right">Balance</th></tr></thead><tbody>
      ${entries.map(e => `<tr><td>${formatDateShort(e.date)}</td><td>${e.description || ''}</td><td>${e.order_number || ''}</td><td>${e.dispatch_number || ''}</td>
        <td class="right">${e.debit > 0 ? formatCurrency(e.debit) : '-'}</td><td class="right green">${e.credit > 0 ? formatCurrency(e.credit) : '-'}</td>
        <td class="right bold ${e.balance > 0 ? 'red' : 'green'}">${formatCurrency(Math.abs(e.balance))}</td></tr>`).join('')}
      </tbody></table>
      <p style="margin-top:10px;font-size:12px">
        <strong>Closing Balance: <span class="${ledgerData.closing_balance > 0 ? 'red' : 'green'}">${formatCurrency(Math.abs(ledgerData.closing_balance))} ${ledgerData.closing_balance > 0 ? '(Due)' : '(Advance)'}</span></strong>
      </p></body></html>`;
    safePrint(htmlContent, `Ledger_${ledgerParty}`);
  };

  const filtered = customers.filter(c => {
    const n = (c.name || '').toLowerCase();
    return n.includes(search.toLowerCase());
  }).slice(0, 20);

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <FileSpreadsheet className="w-4 h-4" />
            {ledgerParty ? (
              <><button onClick={() => { setLedgerData(null); setLedgerParty(''); }} className="text-blue-600 hover:underline text-xs">← Back</button> Ledger - {ledgerParty}</>
            ) : (
              <>Ledger <span className="text-[10px] text-muted-foreground font-normal ml-1">(Alt+L)</span></>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Party Selection */}
        {!ledgerParty ? (
          <div className="space-y-2 flex-1 overflow-hidden flex flex-col">
            <Input placeholder="Search party name..." value={search} onChange={e => setSearch(e.target.value)} autoFocus className="h-8 text-xs shrink-0" data-testid="ledger-search" />
            <ScrollArea className="flex-1">
              {filtered.map(c => (
                <div key={c.id} className="flex items-center justify-between px-3 py-1.5 hover:bg-blue-50 cursor-pointer border-b text-xs transition-colors" onClick={() => openLedger(c.name)} data-testid={`ledger-party-${c.id}`}>
                  <span className="font-medium">{c.name}</span>
                  {c.dispatch_name && c.dispatch_name !== c.name && <span className="text-muted-foreground text-[10px]">{c.dispatch_name}</span>}
                </div>
              ))}
              {filtered.length === 0 && <p className="text-center py-6 text-muted-foreground text-xs">No customers found</p>}
            </ScrollArea>
          </div>
        ) : loadingLedger ? (
          <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : ledgerData ? (
          <div className="flex-1 overflow-hidden flex flex-col space-y-3">
            {/* Closing Balance + PDF */}
            <div className="flex justify-between items-center p-3 bg-secondary/30 rounded-lg shrink-0">
              <span className="text-sm font-medium">Closing Balance:</span>
              <div className="flex items-center gap-2">
                <span className={`text-lg font-bold ${ledgerData.closing_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(Math.abs(ledgerData.closing_balance))} {ledgerData.closing_balance > 0 ? '(Due)' : '(Advance)'}
                </span>
                <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] gap-1" onClick={printLedger}>
                  <FileText className="w-3 h-3" /> PDF
                </Button>
              </div>
            </div>

            {/* Date range filter */}
            <div className="flex items-center gap-2 shrink-0">
              <Label className="text-[10px] text-muted-foreground">From:</Label>
              <DateInputDDMonYY value={ledgerFromDate} onChange={e => setLedgerFromDate(e.target.value)} className="h-7 text-xs w-36" />
              <Label className="text-[10px] text-muted-foreground">To:</Label>
              <DateInputDDMonYY value={ledgerToDate} onChange={e => setLedgerToDate(e.target.value)} className="h-7 text-xs w-36" />
            </div>

            {/* Ledger Table - Scrollable */}
            <div className="flex-1 overflow-auto min-h-0">
              <Table>
                <TableHeader className="sticky top-0 z-10">
                  <TableRow className="bg-secondary/50">
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">Description</TableHead>
                    <TableHead className="text-xs">Order No</TableHead>
                    <TableHead className="text-xs">Dispatch No</TableHead>
                    <TableHead className="text-xs text-right">Debit (Sale)</TableHead>
                    <TableHead className="text-xs text-right">Credit (Receipt)</TableHead>
                    <TableHead className="text-xs text-right">Balance</TableHead>
                    <TableHead className="text-xs text-center w-24">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(ledgerData.entries || [])
                    .filter(entry => {
                      if (ledgerFromDate && entry.date < ledgerFromDate) return false;
                      if (ledgerToDate && entry.date > ledgerToDate) return false;
                      return true;
                    })
                    .map((entry, idx) => (
                    <React.Fragment key={`${entry.type}-${entry.date}-${idx}`}>
                    <TableRow className={entry.type === 'receipt' ? 'bg-green-50/50' : ''}>
                      <TableCell className="text-xs">
                        {entry.type === 'receipt' && editingReceiptDate === entry.receipt_id ? (
                          <DateInputDDMonYY value={newReceiptDate} onChange={e => { setNewReceiptDate(e.target.value); updateReceiptDate(entry.receipt_id, e.target.value); }} className="h-6 text-xs w-32" />
                        ) : (
                          <span className={entry.type === 'receipt' ? 'cursor-pointer hover:underline' : ''} onClick={() => { if (entry.type === 'receipt') { setEditingReceiptDate(entry.receipt_id); setNewReceiptDate(entry.date); } }}>
                            {formatDateShort(entry.date)}
                            {entry.type === 'receipt' && <Edit className="w-2.5 h-2.5 inline ml-1 text-muted-foreground" />}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {entry.type === 'sale' ? (
                          <span className="cursor-pointer text-blue-600 hover:underline font-medium" onClick={() => viewBillDetail(entry.reference)}>
                            {entry.description}
                          </span>
                        ) : entry.description}
                      </TableCell>
                      <TableCell className="text-xs font-mono">{entry.order_number || '-'}</TableCell>
                      <TableCell className="text-xs font-mono text-blue-600">{entry.dispatch_number || '-'}</TableCell>
                      <TableCell className="text-xs text-right">{entry.debit > 0 ? formatCurrency(entry.debit) : '-'}</TableCell>
                      <TableCell className="text-xs text-right">
                        {entry.credit > 0 ? (
                          editingReceiptAmount === entry.receipt_id ? (
                            <Input type="number" value={newReceiptAmount} onChange={e => setNewReceiptAmount(e.target.value)} onBlur={() => updateReceiptAmount(entry.receipt_id, newReceiptAmount)} onKeyDown={e => e.key === 'Enter' && updateReceiptAmount(entry.receipt_id, newReceiptAmount)} className="h-6 text-xs w-24 text-right" autoFocus />
                          ) : (
                            <span className="text-green-600 cursor-pointer hover:underline" onClick={() => { setEditingReceiptAmount(entry.receipt_id); setNewReceiptAmount(String(entry.credit)); }}>
                              {formatCurrency(entry.credit)} <Edit className="w-2.5 h-2.5 inline text-muted-foreground" />
                            </span>
                          )
                        ) : '-'}
                      </TableCell>
                      <TableCell className={`text-xs text-right font-medium ${entry.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(Math.abs(entry.balance))}</TableCell>
                      <TableCell className="text-xs text-center">
                        {entry.type === 'receipt' && (
                          <div className="flex items-center justify-center gap-1">
                            <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[9px] text-blue-500 hover:text-blue-700 hover:bg-blue-50" onClick={() => openLedgerReceiptEdit(entry.receipt_id)}>
                              <Edit className="w-2.5 h-2.5 mr-0.5" /> Edit
                            </Button>
                            <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[9px] text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => reverseReceipt(entry.receipt_id)}>
                              <Trash2 className="w-2.5 h-2.5 mr-0.5" /> Reverse
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                    {/* Inline receipt edit row */}
                    {entry.type === 'receipt' && ledgerEditReceipt === entry.receipt_id && (
                      <TableRow className="bg-blue-50/70">
                        <TableCell colSpan={8} className="p-2">
                          <div className="grid grid-cols-7 gap-2 items-end">
                            <div><Label className="text-[9px] text-muted-foreground">Date</Label>
                              <DateInputDDMonYY value={ledgerEditForm.date} onChange={e => setLedgerEditForm({...ledgerEditForm, date: e.target.value})} className="h-7 text-xs" /></div>
                            <div><Label className="text-[9px] text-muted-foreground">Amount</Label>
                              <Input type="number" value={ledgerEditForm.amount} onChange={e => setLedgerEditForm({...ledgerEditForm, amount: e.target.value})} className="h-7 text-xs" /></div>
                            <div><Label className="text-[9px] text-muted-foreground">Account</Label>
                              <Input value={ledgerEditForm.debit_account} onChange={e => setLedgerEditForm({...ledgerEditForm, debit_account: e.target.value})} className="h-7 text-xs" /></div>
                            <div><Label className="text-[9px] text-muted-foreground">Reference</Label>
                              <Input value={ledgerEditForm.reference} onChange={e => setLedgerEditForm({...ledgerEditForm, reference: e.target.value})} className="h-7 text-xs" placeholder="CHQ/UTR..." /></div>
                            <div><Label className="text-[9px] text-muted-foreground">Narration</Label>
                              <Input value={ledgerEditForm.narration} onChange={e => setLedgerEditForm({...ledgerEditForm, narration: e.target.value})} className="h-7 text-xs" /></div>
                            <div><Label className="text-[9px] text-muted-foreground">Order No(s)</Label>
                              <Input value={ledgerEditForm.order_numbers} onChange={e => setLedgerEditForm({...ledgerEditForm, order_numbers: e.target.value})} className="h-7 text-xs" placeholder="E03001, E03002" /></div>
                            <div className="flex gap-1">
                              <Button size="sm" className="h-7 text-xs px-3" onClick={saveLedgerReceiptEdit}><Save className="w-3 h-3 mr-1" />Save</Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => setLedgerEditReceipt(null)}>Cancel</Button>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Add Transaction - Collapsible */}
            <div className="border rounded-lg bg-amber-50/50 shrink-0">
              <button className="w-full p-2 text-xs font-semibold flex items-center gap-1 hover:bg-amber-100/50 rounded-t-lg" onClick={() => setShowAddTxn(!showAddTxn)}>
                {showAddTxn ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                <Plus className="w-3 h-3" /> Add Transaction
              </button>
              {showAddTxn && (
                <div className="p-3 pt-0">
                  <div className="grid grid-cols-5 gap-2">
                    <DateInputDDMonYY value={journalForm.date} onChange={e => setJournalForm({...journalForm, date: e.target.value})} className="h-7 text-xs" />
                    <Input type="number" placeholder="Amount" value={journalForm.amount} onChange={e => setJournalForm({...journalForm, amount: e.target.value})} className="h-7 text-xs" />
                    <Select value={journalForm.from_account || ledgerParty} onValueChange={v => setJournalForm({...journalForm, from_account: v})}>
                      <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Credit (From)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ledgerParty} className="text-xs font-medium">{ledgerParty} (current)</SelectItem>
                        {customers.filter(c => c.name !== ledgerParty).slice(0, 20).map(c => <SelectItem key={c.id} value={c.name} className="text-xs">{c.name}</SelectItem>)}
                        <SelectItem value="Bank" className="text-xs">Bank</SelectItem>
                        <SelectItem value="Msk" className="text-xs">Msk</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={journalForm.to_account} onValueChange={v => setJournalForm({...journalForm, to_account: v})}>
                      <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Debit (To)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Bank" className="text-xs">Bank</SelectItem>
                        <SelectItem value="Msk" className="text-xs">Msk</SelectItem>
                        <SelectItem value="Return Goods" className="text-xs">Return Goods</SelectItem>
                        <SelectItem value="Write Off Balance" className="text-xs">Write Off Balance</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" onClick={() => { if (!journalForm.from_account) setJournalForm(prev => ({...prev, from_account: ledgerParty})); saveJournalEntry(); }} disabled={savingJournal} className="h-7 text-xs">
                      {savingJournal ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save
                    </Button>
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    <Input placeholder="Reference (CHQ/UTR)" value={journalForm.reference} onChange={e => setJournalForm({...journalForm, reference: e.target.value})} className="h-7 text-xs" />
                    <Input placeholder="Order No(s)" value={journalForm.order_numbers} onChange={e => setJournalForm({...journalForm, order_numbers: e.target.value})} className="h-7 text-xs" />
                    <Input placeholder="Dispatch No(s)" value={journalForm.dispatch_numbers} onChange={e => setJournalForm({...journalForm, dispatch_numbers: e.target.value})} className="h-7 text-xs" />
                    <Input placeholder="Narration" value={journalForm.narration} onChange={e => setJournalForm({...journalForm, narration: e.target.value})} className="h-7 text-xs" />
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>

    {/* Bill Detail Dialog */}
    <Dialog open={billDetailOpen} onOpenChange={setBillDetailOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5" /> Order Summary</DialogTitle>
        </DialogHeader>
        {loadingBill ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : billDetailOrder ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-muted-foreground">Order:</span> <strong>{billDetailOrder.order_number}</strong></div>
              <div><span className="text-muted-foreground">Date:</span> <strong>{formatDateShort(billDetailOrder.order_date)}</strong></div>
              <div><span className="text-muted-foreground">Customer:</span> <strong>{billDetailOrder.customer_name}</strong></div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="text-xs">Grade</TableHead>
                  <TableHead className="text-xs">SKU</TableHead>
                  <TableHead className="text-xs text-right">Qty</TableHead>
                  <TableHead className="text-xs text-right">Rate</TableHead>
                  <TableHead className="text-xs text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {billDetailOrder.items?.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="text-xs">{item.grade}</TableCell>
                    <TableCell className="text-xs">{item.sku}</TableCell>
                    <TableCell className="text-xs text-right">{item.qty}</TableCell>
                    <TableCell className="text-xs text-right">{formatCurrency(item.rate)}</TableCell>
                    <TableCell className="text-xs text-right font-medium">{formatCurrency(item.amount || item.qty * item.rate)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-secondary/30 font-bold">
                  <TableCell colSpan={2} className="text-xs">TOTAL</TableCell>
                  <TableCell className="text-xs text-right">{billDetailOrder.items?.reduce((s, i) => s + (i.qty || 0), 0)}</TableCell>
                  <TableCell className="text-xs text-right"></TableCell>
                  <TableCell className="text-xs text-right">{formatCurrency(billDetailOrder.total_value)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        ) : <p className="text-center text-muted-foreground py-4">Order not found</p>}
      </DialogContent>
    </Dialog>
    </>
  );
}
